import React, { useState, useRef } from "react";
import { Upload, X, FileText, Image as ImgIcon, FileArchive } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ALLOWED = [".jpg", ".jpeg", ".png", ".pdf", ".zip"];
const MAX_BYTES = 10 * 1024 * 1024;

const iconFor = (name) => {
  const ext = (name.split(".").pop() || "").toLowerCase();
  if (["jpg","jpeg","png"].includes(ext)) return ImgIcon;
  if (ext === "zip") return FileArchive;
  return FileText;
};

const fmtSize = (b) => b < 1024 ? `${b}B` : b < 1024*1024 ? `${(b/1024).toFixed(1)}KB` : `${(b/1024/1024).toFixed(1)}MB`;

export default function AttachmentUploader({ ticketId, onUploaded, attachments = [], canDelete = false }) {
  const [drag, setDrag] = useState(false);
  const [queue, setQueue] = useState([]); // {file, progress}
  const inputRef = useRef(null);

  const validate = (file) => {
    const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
    if (!ALLOWED.includes(ext)) { toast.error(`${file.name}: type not allowed`); return false; }
    if (file.size > MAX_BYTES) { toast.error(`${file.name}: > 10MB`); return false; }
    return true;
  };

  const upload = async (files) => {
    const valid = [...files].filter(validate);
    if (!valid.length) return;
    setQueue(valid.map(f => ({ file: f, progress: 0 })));
    const fd = new FormData();
    valid.forEach(f => fd.append("files", f));
    try {
      const token = localStorage.getItem("pgac_token");
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${process.env.REACT_APP_BACKEND_URL}/api/tickets/${ticketId}/attachments`);
      xhr.setRequestHeader("Authorization", `Bearer ${token}`);
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          const pct = Math.round((e.loaded / e.total) * 100);
          setQueue(q => q.map(x => ({ ...x, progress: pct })));
        }
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          toast.success(`Uploaded ${valid.length} file(s)`);
          setQueue([]);
          onUploaded?.(JSON.parse(xhr.responseText));
        } else {
          let msg = "Upload failed";
          try { msg = JSON.parse(xhr.responseText).detail || msg; } catch {}
          toast.error(msg);
          setQueue([]);
        }
      };
      xhr.onerror = () => { toast.error("Upload failed"); setQueue([]); };
      xhr.send(fd);
    } catch (e) { toast.error("Upload failed"); setQueue([]); }
  };

  const removeAttachment = async (id) => {
    if (!window.confirm("Delete this attachment?")) return;
    try {
      const r = await api.delete(`/tickets/${ticketId}/attachments/${id}`);
      onUploaded?.(r.data);
      toast.success("Attachment removed");
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-3" data-testid="attachment-uploader">
      <div
        data-testid="dropzone"
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); upload(e.dataTransfer.files); }}
        onClick={() => inputRef.current?.click()}
        className={`border-2 border-dashed p-6 text-center cursor-pointer transition-colors ${drag ? "border-[#FF5000] bg-[#FFF4EB]" : "border-border bg-muted/40"}`}
      >
        <Upload size={20} className="mx-auto text-muted-foreground"/>
        <div className="font-head font-bold text-sm mt-2 uppercase tracking-wider">Drop files or click</div>
        <div className="data-label mt-1">jpg · png · pdf · zip · max 10MB</div>
        <input ref={inputRef} type="file" multiple hidden accept=".jpg,.jpeg,.png,.pdf,.zip"
               onChange={(e) => upload(e.target.files)} data-testid="file-input"/>
      </div>

      {queue.map((q, i) => (
        <div key={i} className="border border-border p-2 text-xs">
          <div className="flex items-center justify-between"><span className="font-mono truncate">{q.file.name}</span><span>{q.progress}%</span></div>
          <div className="h-1 bg-muted mt-1"><div className="h-full bg-[#FF5000] transition-all" style={{ width: `${q.progress}%` }}/></div>
        </div>
      ))}

      {attachments.length > 0 && (
        <div className="space-y-1" data-testid="attachment-list">
          {attachments.map(a => {
            const Icon = iconFor(a.fileName);
            return (
              <div key={a.id} className="flex items-center justify-between border border-border bg-white p-2 text-xs hover:bg-muted">
                <a href={`${process.env.REACT_APP_BACKEND_URL}${a.filePath}`} target="_blank" rel="noreferrer"
                   className="flex items-center gap-2 min-w-0 flex-1 hover:text-[#FF5000]" data-testid={`attachment-${a.id}`}>
                  <Icon size={14} className="shrink-0"/>
                  <span className="font-mono truncate">{a.fileName}</span>
                  <span className="text-muted-foreground shrink-0">{fmtSize(a.fileSize || 0)}</span>
                </a>
                {canDelete && (
                  <button onClick={() => removeAttachment(a.id)} className="hover:text-[#FF3B30] ml-2" title="Delete" data-testid={`delete-attachment-${a.id}`}>
                    <X size={14}/>
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
